/**
 * Which machine or line a conversation is about. The chat API stores no
 * context with a session, so the assistant keeps the contextId of the
 * conversations it starts here (localStorage, this browser only). The shared
 * assistant uses it to reopen a machine's conversation when the user comes
 * back to that machine, and to mark context conversations in the list.
 *
 * Only contextIds are stored, never the context text: the text sent to the
 * agent is always rebuilt from the SAP store (useCockpitContext).
 */

import { parseContextId } from '../../../hooks/useCockpitContext';

const KEY = 'pc.assistant.sessionContexts.v1';
/** Newest entries kept; older conversations simply count as general. */
const MAX_ENTRIES = 300;

function read(): Record<string, string> {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(KEY) || '{}');
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function write(all: Record<string, string>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    // private mode or full storage: the context is kept for this page only
  }
}

export function rememberSessionContext(sessionId: string, contextId: string | undefined): void {
  if (!contextId || !parseContextId(contextId)) return; // all data: nothing to remember
  const all = read();
  delete all[sessionId]; // re-insert as the newest
  all[sessionId] = contextId;
  const keys = Object.keys(all);
  for (const k of keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES))) delete all[k];
  write(all);
}

export function sessionContextOf(sessionId: string): string | undefined {
  const v = read()[sessionId];
  return typeof v === 'string' && parseContextId(v) ? v : undefined;
}

export function forgetSessionContext(sessionId: string): void {
  const all = read();
  if (!(sessionId in all)) return;
  delete all[sessionId];
  write(all);
}
